export const MAX_PIN_ATTEMPTS = 5;
export const PIN_COOLDOWN_MS = 30_000;
export const PARENT_ATTEMPT_STORAGE_KEY = "perfect-draw-parent-attempts-v1";

interface AttemptState {
  failures: number;
  blockedUntil: number;
}

export interface AttemptStatus {
  allowed: boolean;
  failures: number;
  blockedUntil: number;
  remainingMs: number;
}

function validState(value: unknown, now: number): AttemptState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).length !== 3 ||
    record.version !== 1 ||
    !Number.isInteger(record.failures) ||
    (record.failures as number) < 0 ||
    (record.failures as number) > MAX_PIN_ATTEMPTS ||
    !Number.isFinite(record.blockedUntil) ||
    (record.blockedUntil as number) < 0 ||
    ((record.failures as number) < MAX_PIN_ATTEMPTS && record.blockedUntil !== 0) ||
    ((record.failures as number) === MAX_PIN_ATTEMPTS &&
      ((record.blockedUntil as number) === 0 ||
        (record.blockedUntil as number) > now + PIN_COOLDOWN_MS))
  ) {
    return null;
  }
  return {
    failures: record.failures as number,
    blockedUntil: record.blockedUntil as number,
  };
}

export class AttemptController {
  private state: AttemptState = { failures: 0, blockedUntil: 0 };

  public constructor(
    private readonly storage: Storage | null,
    private readonly now: () => number = Date.now,
  ) {
    this.state = this.read();
  }

  public getStatus(): AttemptStatus {
    const currentTime = this.now();
    this.state = this.read(this.state, currentTime);
    if (this.state.blockedUntil > 0 && currentTime >= this.state.blockedUntil) {
      this.state = { failures: 0, blockedUntil: 0 };
      this.persist();
    }
    const remainingMs = Math.max(0, this.state.blockedUntil - currentTime);
    return {
      allowed: remainingMs === 0,
      failures: this.state.failures,
      blockedUntil: this.state.blockedUntil,
      remainingMs,
    };
  }

  public recordFailure(): AttemptStatus {
    const status = this.getStatus();
    if (!status.allowed) return status;
    const failures = Math.min(MAX_PIN_ATTEMPTS, this.state.failures + 1);
    this.state = {
      failures,
      blockedUntil: failures >= MAX_PIN_ATTEMPTS ? this.now() + PIN_COOLDOWN_MS : 0,
    };
    this.persist();
    return this.getStatus();
  }

  public reset(): void {
    this.state = { failures: 0, blockedUntil: 0 };
    try {
      this.storage?.removeItem(PARENT_ATTEMPT_STORAGE_KEY);
    } catch {
      // A failed cleanup must not retain an attempted PIN or unlock the session.
    }
  }

  private read(
    fallback: AttemptState = { failures: 0, blockedUntil: 0 },
    currentTime = this.now(),
  ): AttemptState {
    if (!this.storage) return fallback;
    try {
      const raw = this.storage.getItem(PARENT_ATTEMPT_STORAGE_KEY);
      if (raw === null) return { failures: 0, blockedUntil: 0 };
      const parsed = validState(JSON.parse(raw) as unknown, currentTime);
      if (parsed) return parsed;
      this.storage.removeItem(PARENT_ATTEMPT_STORAGE_KEY);
    } catch {
      try {
        this.storage.removeItem(PARENT_ATTEMPT_STORAGE_KEY);
      } catch {
        // Ignore unavailable session storage and stay locked.
      }
    }
    return fallback;
  }

  private persist(): void {
    if (!this.storage) return;
    try {
      if (this.state.failures === 0 && this.state.blockedUntil === 0) {
        this.storage.removeItem(PARENT_ATTEMPT_STORAGE_KEY);
      } else {
        this.storage.setItem(
          PARENT_ATTEMPT_STORAGE_KEY,
          JSON.stringify({ version: 1, ...this.state }),
        );
      }
    } catch {
      // Cooldown storage is best effort; the in-memory gate remains active.
    }
  }
}
